import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Building2, Sparkles, CheckCircle, ArrowRight, Rocket, Users, ClipboardCheck, ChevronRight } from 'lucide-react';
import { fireSuccessConfetti, fireCelebration } from '@/lib/confetti';

interface OnboardingModalProps {
  open: boolean;
  onComplete: () => void;
}

const features = [
  {
    icon: ClipboardCheck,
    title: 'Create & assign tasks',
    description: 'Manage work with AI assistance',
    color: 'from-primary to-accent',
  },
  {
    icon: Users,
    title: 'Coordinate your team',
    description: 'Track capacity and skills',
    color: 'from-info to-primary',
  },
  {
    icon: Rocket,
    title: 'Process approvals',
    description: 'Streamline requests instantly',
    color: 'from-accent to-primary',
  },
];

export function OnboardingModal({ open, onComplete }: OnboardingModalProps) {
  const [step, setStep] = useState(1);
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { startTour } = useOnboarding();
  const { toast } = useToast();

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .slice(0, 50);
  };

  const handleCreateOrganization = async () => {
    if (!orgName.trim() || !user) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-organization', {
        body: {
          name: orgName.trim(),
          slug: generateSlug(orgName.trim())
        }
      });

      if (error) throw error;

      // Fire confetti on success!
      fireSuccessConfetti();
      setStep(3);
    } catch (error) {
      console.error('Error creating organization:', error);
      toast({
        title: 'Error',
        description: 'Failed to create organization. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = (skipTour: boolean) => {
    // Fire celebration confetti!
    fireCelebration();
    
    toast({
      title: '🎉 Welcome!',
      description: `${orgName} has been created. You're all set!`
    });
    onComplete();
    if (!skipTour) {
      setTimeout(() => {
        startTour();
      }, 500);
    }
  };

  // Animated background particles
  const particles = Array.from({ length: 20 }).map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 4 + 2,
    duration: Math.random() * 3 + 2,
  }));

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-[520px] p-0 overflow-hidden border-border/30 bg-card/95 backdrop-blur-2xl" 
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Gradient orbs */}
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{ duration: 4, repeat: Infinity }}
            className="absolute -top-20 -right-20 w-60 h-60 bg-primary/20 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1.2, 1, 1.2],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{ duration: 5, repeat: Infinity }}
            className="absolute -bottom-20 -left-20 w-60 h-60 bg-accent/20 rounded-full blur-3xl"
          />
          
          {/* Floating particles */}
          {particles.map((particle) => (
            <motion.div
              key={particle.id}
              animate={{
                y: [0, -30, 0],
                opacity: [0.2, 0.5, 0.2],
              }}
              transition={{
                duration: particle.duration,
                repeat: Infinity,
                delay: particle.id * 0.1,
              }}
              className="absolute rounded-full bg-primary/30"
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                width: particle.size,
                height: particle.size,
              }}
            />
          ))}
        </div>

        <div className="relative p-8">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                {/* Logo */}
                <motion.div 
                  className="flex justify-center"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
                >
                  <div className="relative">
                    <div className="w-20 h-20 rounded-2xl gradient-primary flex items-center justify-center shadow-glow">
                      <Sparkles className="w-10 h-10 text-primary-foreground" />
                    </div>
                    {/* Animated ring */}
                    <motion.div
                      animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="absolute inset-0 rounded-2xl border-2 border-primary"
                    />
                  </div>
                </motion.div>

                {/* Title */}
                <motion.div 
                  className="text-center space-y-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <h2 className="text-3xl font-bold gradient-text">Welcome to OpsGPT!</h2>
                  <p className="text-muted-foreground">
                    Your AI-powered operations assistant is ready to transform your workflow.
                  </p>
                </motion.div>

                {/* Features */}
                <div className="space-y-3">
                  {features.map((feature, index) => (
                    <motion.div
                      key={feature.title}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      whileHover={{ x: 4, transition: { duration: 0.2 } }}
                      className="flex items-center gap-4 p-4 rounded-xl bg-secondary/30 border border-border/30 hover:border-primary/30 transition-colors cursor-default"
                    >
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center shrink-0 shadow-lg`}>
                        <feature.icon className="w-6 h-6 text-primary-foreground" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-foreground">{feature.title}</p>
                        <p className="text-sm text-muted-foreground">{feature.description}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </motion.div>
                  ))}
                </div>

                {/* CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <Button 
                    onClick={() => setStep(2)} 
                    variant="glow" 
                    size="lg" 
                    className="w-full group"
                  >
                    Get Started
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </motion.div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                {/* Icon */}
                <motion.div 
                  className="flex justify-center"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 border border-primary/30 flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-primary" />
                  </div>
                </motion.div>

                {/* Title */}
                <motion.div 
                  className="text-center space-y-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <h2 className="text-2xl font-bold text-foreground">Create your workspace</h2>
                  <p className="text-muted-foreground">
                    This is where you'll manage your team and projects.
                  </p>
                </motion.div>

                {/* Form */}
                <motion.div 
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="space-y-2">
                    <Label htmlFor="orgName" className="text-sm font-medium">
                      Organization Name
                    </Label>
                    <Input
                      id="orgName"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      placeholder="Acme Corp"
                      className="h-12 text-base bg-secondary/50 border-border/50 focus:border-primary/50"
                      autoFocus
                    />
                    {orgName && (
                      <motion.p 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="text-xs text-muted-foreground flex items-center gap-1"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-success" />
                        Workspace URL: {generateSlug(orgName) || 'your-org'}
                      </motion.p>
                    )}
                  </div>
                </motion.div>

                {/* Actions */}
                <motion.div 
                  className="flex gap-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Button 
                    variant="outline" 
                    onClick={() => setStep(1)} 
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button 
                    onClick={handleCreateOrganization}
                    disabled={loading || !orgName.trim()}
                    variant="glow"
                    className="flex-1"
                  >
                    {loading ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full"
                      />
                    ) : (
                      <>Create Workspace</>
                    )}
                  </Button>
                </motion.div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, type: "spring" }}
                className="space-y-6 text-center"
              >
                {/* Success Icon */}
                <motion.div 
                  className="flex justify-center"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
                >
                  <div className="relative">
                    <motion.div 
                      className="w-24 h-24 rounded-full bg-success/10 flex items-center justify-center"
                      animate={{ 
                        boxShadow: [
                          '0 0 0 0 rgba(16, 185, 129, 0.4)',
                          '0 0 0 20px rgba(16, 185, 129, 0)',
                        ]
                      }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <CheckCircle className="w-12 h-12 text-success" />
                    </motion.div>
                  </div>
                </motion.div>

                {/* Title */}
                <motion.div 
                  className="space-y-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <h2 className="text-3xl font-bold text-foreground">You're all set! 🎉</h2>
                  <p className="text-muted-foreground">
                    <span className="font-semibold text-foreground">{orgName}</span> has been created successfully.
                  </p>
                </motion.div>

                {/* Tour prompt */}
                <motion.div 
                  className="p-4 rounded-xl bg-secondary/30 border border-border/30"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <p className="text-sm text-muted-foreground">
                    Would you like a quick tour of the main features?
                  </p>
                </motion.div>

                {/* Actions */}
                <motion.div 
                  className="space-y-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <Button 
                    onClick={() => handleFinish(false)} 
                    variant="glow"
                    size="lg"
                    className="w-full group"
                  >
                    <Sparkles className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                    Take the tour (2 min)
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => handleFinish(true)} 
                    className="w-full text-muted-foreground hover:text-foreground"
                  >
                    Skip for now
                  </Button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Step indicators */}
          <motion.div 
            className="flex justify-center gap-2 mt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            {[1, 2, 3].map((s) => (
              <motion.div
                key={s}
                className={cn(
                  "h-1.5 rounded-full transition-all duration-300",
                  s === step 
                    ? "w-8 bg-primary" 
                    : s < step 
                    ? "w-4 bg-primary/50" 
                    : "w-4 bg-muted"
                )}
                animate={s === step ? { scale: [1, 1.1, 1] } : {}}
                transition={{ duration: 0.3 }}
              />
            ))}
          </motion.div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
